import FeaturesMap from '@apps-common/ui-maps/FeaturesMap.jsx';
import React, {useMemo} from 'react';
import {Alert, Box, Button, Tooltip} from '@mui/material';
import {
  DATASETS,
  DEFAULT_DATASET,
} from '@apps-common/geocorn-sdk/api/datasets.js';
import {array, bool, func, object, string} from 'prop-types';
import {useTranslation} from 'react-i18next';

import ButtonBar from './ButtonBar.jsx';
import CollectionForm from './CollectionForm.jsx';
import FeaturePropsForm from './FeaturePropsForm.jsx';
import FeaturesList from './FeaturesList.jsx';
import {Content, MapPanel, Sidebar} from './Common.jsx';
import {MAX_FEATURES} from './import.js';

const SelectStep = ({
  collection,
  featureProperties,
  features,
  importing,
  onBack,
  onFinished,
  onVerify,
  selectedFeatures,
  setCollection,
  setFeatureProperties,
  setSelectedFeatures,
  maxFeaturesWarning,
}) => {
  const {t} = useTranslation();

  const someInvalid = useMemo(
    () => selectedFeatures.some(f => !f.pl_metadata.is_clean),
    [selectedFeatures],
  );

  return (
    <>
      <Content>
        <Box display="flex" width="100%">
          <MapPanel>
            <FeaturesMap
              features={features}
              selectedFeatures={selectedFeatures}
              featuresStyle="unselected"
            />
          </MapPanel>
          <Sidebar>
            {DEFAULT_DATASET !== DATASETS.AOA && (
              <CollectionForm
                collection={collection}
                setCollection={setCollection}
              />
            )}
            <FeaturePropsForm
              features={features}
              featureProperties={featureProperties}
              setFeatureProperties={setFeatureProperties}
            />
            <FeaturesList
              features={features}
              featureProperties={featureProperties}
              selectedFeatures={selectedFeatures}
              setSelectedFeatures={setSelectedFeatures}
            />
          </Sidebar>
        </Box>
      </Content>
      <ButtonBar onBack={onBack}>
        {maxFeaturesWarning && (
          <Alert
            severity="warning"
            sx={{padding: theme => theme.spacing(0, 1), marginRight: 'auto'}}
          >
            {t(maxFeaturesWarning, {max: MAX_FEATURES})}
          </Alert>
        )}
        {someInvalid && (
          <Button
            variant="outlined"
            color="primary"
            onClick={() => {
              onVerify();
            }}
            disabled={selectedFeatures.length === 0 || !collection || importing}
          >
            Simplify manually
          </Button>
        )}
        <Tooltip
          title={
            !importing && someInvalid
              ? 'Automatically simplify with the least area change'
              : ''
          }
        >
          <span>
            <Button
              variant="contained"
              color="primary"
              data-testid="create-areas-btn"
              onClick={() => {
                onFinished();
              }}
              disabled={
                selectedFeatures.length === 0 || !collection || importing
              }
            >
              {importing
                ? 'Importing...'
                : someInvalid
                  ? 'Fix and import'
                  : 'Import'}
            </Button>
          </span>
        </Tooltip>
      </ButtonBar>
    </>
  );
};

SelectStep.propTypes = {
  collection: object,
  featureProperties: object,
  features: array,
  importing: bool,
  onBack: func,
  onFinished: func,
  onVerify: func,
  selectedFeatures: array,
  setCollection: func,
  setFeatureProperties: func,
  setSelectedFeatures: func,
  maxFeaturesWarning: string,
};

export default SelectStep;
